import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { Toaster } from "react-hot-toast";

import { io, type Socket } from "socket.io-client";

import { getApiBaseUrl } from "../services/authApi";

import { getAccessToken } from "../services/authToken";

import {
  getNotificationSettings,
  saveNotificationSettings as postNotificationSettings,
} from "../services/notificationSettingsApi";

import { buildDealUpdateNotification } from "../utils/buildDealUpdateNotification";
import {
  isDealUpdatePayload,
  showDealUpdateToast,
} from "../utils/dealUpdateToast";
import { shouldShowDealUpdateNotification } from "../utils/shouldShowDealUpdateNotification";

import { useAuthContext } from "./AuthContext";

import type { AuthUserSummary } from "@/types/auth";
import type { Notification } from "@/types/dealTask";
import type { Dispatch, SetStateAction } from "react";

const DEFAULT_SHOW_NOTIFICATIONS = true;

const DEAL_UPDATE_NOTIFICATION_DELAY_MS = 20_000;

// Defensive cap: messages are pruned when their bell notification is queued
// (or their toast is dismissed), so this should never normally be reached.
const MAX_LAST_MESSAGES = 50;

/** Server events (plus "connect", fired on every connect and reconnect) a page can subscribe to. */
export type SocketEventName =
  | "connect"
  | "deal_updated"
  | "accordion_updated"
  | "deal_created"
  | "deal_deleted"
  | "advisor_fees_updated";

/** Server events that are only forwarded to page subscribers (no toast or bell). */
const FORWARDED_SOCKET_EVENTS = [
  "accordion_updated",
  "deal_created",
  "deal_deleted",
  "advisor_fees_updated",
] as const satisfies readonly SocketEventName[];

export type SocketEventListener = (data?: unknown) => void;

interface SocketContextValue {
  isConnected: boolean;

  lastMessage: unknown[];

  userSummary: AuthUserSummary | null;

  showNotifications: boolean;

  notificationSettingsLoading: boolean;

  notificationSettingsLoadError: string | null;

  savingNotificationSettings: boolean;

  saveNotificationSettingsError: string | null;

  saveNotificationSettings: (showNotifications: boolean) => Promise<boolean>;

  handleDealUpdated: (data: unknown) => void;

  subscribe: (
    event: SocketEventName,
    listener: SocketEventListener,
  ) => () => void;

  /** Hide deal update toasts until the returned release function is called. */
  suppressDealUpdateToasts: () => () => void;
}

const SocketContext = createContext<SocketContextValue | undefined>(undefined);

interface SocketProviderProps {
  children: ReactNode;
  notifications: Notification[];
  setNotifications: Dispatch<SetStateAction<Notification[]>>;
}

export function SocketProvider({
  children,
  notifications: _notifications,
  setNotifications,
}: SocketProviderProps) {
  const { userSummary } = useAuthContext();

  const [isConnected, setIsConnected] = useState(false);

  const [lastMessage, setLastMessage] = useState<unknown[]>([]);
  const pendingNotificationTimeoutsRef = useRef<
    ReturnType<typeof setTimeout>[]
  >([]);
  const queueDealUpdateNotificationRef = useRef<(data: unknown) => void>(
    () => undefined,
  );
  const handleDealUpdatedRef = useRef<(data: unknown) => void>(
    () => undefined,
  );
  // Deal updates whose toast the user closed; their delayed bell notification
  // is skipped, as before (closing the toast acknowledges the update).
  const dismissedDealUpdatesRef = useRef(new WeakSet<object>());
  const listenersRef = useRef(new Map<SocketEventName, Set<SocketEventListener>>());
  // Pages showing live data (the Pipeline) replace toasts with in-place
  // highlights; the delayed bell notification is still queued.
  const toastSuppressionCountRef = useRef(0);

  // The socket should only be torn down when the signed-in user changes, not
  // whenever the userSummary object identity changes.
  const socketUserKey = userSummary ? userSummary.email : null;

  const [showNotifications, setShowNotifications] = useState(
    DEFAULT_SHOW_NOTIFICATIONS,
  );

  const [notificationSettingsLoading, setNotificationSettingsLoading] =
    useState(true);

  const [notificationSettingsLoadError, setNotificationSettingsLoadError] =
    useState<string | null>(null);

  const [savingNotificationSettings, setSavingNotificationSettings] =
    useState(false);

  const [saveNotificationSettingsError, setSaveNotificationSettingsError] =
    useState<string | null>(null);

  const dismissDealUpdate = useCallback((message: unknown) => {
    if (typeof message === "object" && message !== null) {
      dismissedDealUpdatesRef.current.add(message);
    }

    setLastMessage((prev) => removeMessage(prev, message));
  }, []);

  const queueDealUpdateNotification = useCallback(
    (data: unknown) => {
      if (!isDealUpdatePayload(data)) {
        return;
      }

      // Apply the same per-user filter as the toast so the delayed bell
      // notification respects the show_notifications setting.
      if (
        !shouldShowDealUpdateNotification(
          data,
          userSummary?.displayName ?? "",
          showNotifications,
        )
      ) {
        return;
      }

      // The bell's unread count is derived from unread local notifications
      // in NotificationsProvider, so adding it here is enough.
      setNotifications((prev) => [buildDealUpdateNotification(data), ...prev]);
    },
    [setNotifications, showNotifications, userSummary?.displayName],
  );

  const subscribe = useCallback(
    (event: SocketEventName, listener: SocketEventListener) => {
      const listeners = listenersRef.current.get(event) ?? new Set();
      listeners.add(listener);
      listenersRef.current.set(event, listeners);

      return () => {
        listeners.delete(listener);
      };
    },
    [],
  );

  const suppressDealUpdateToasts = useCallback(() => {
    toastSuppressionCountRef.current += 1;
    let released = false;

    return () => {
      if (released) return;
      released = true;
      toastSuppressionCountRef.current -= 1;
    };
  }, []);

  const handleDealUpdated = useCallback(
    (data: unknown) => {
      const displayName = userSummary?.displayName ?? "";

      if (toastSuppressionCountRef.current > 0) {
        return;
      }

      if (
        !shouldShowDealUpdateNotification(data, displayName, showNotifications)
      ) {
        return;
      }

      showDealUpdateToast(data, () => dismissDealUpdate(data));
    },
    [
      dismissDealUpdate,
      showNotifications,
      userSummary?.displayName,
    ],
  );

  // Keep the socket listener pointed at the latest callbacks without making the
  // socket effect depend on them (which would reconnect the socket and drop
  // pending notification timers whenever notification settings change).
  useLayoutEffect(() => {
    queueDealUpdateNotificationRef.current = queueDealUpdateNotification;
    handleDealUpdatedRef.current = handleDealUpdated;
  }, [queueDealUpdateNotification, handleDealUpdated]);

  const saveNotificationSettings = useCallback(
    async (nextShowNotifications: boolean) => {
      setSavingNotificationSettings(true);

      setSaveNotificationSettingsError(null);

      const response = await postNotificationSettings({
        show_notifications: nextShowNotifications,
      });

      if (response.success) {
        setShowNotifications(
          response.data?.show_notifications ?? nextShowNotifications,
        );

        setSavingNotificationSettings(false);

        return true;
      }

      setSaveNotificationSettingsError(
        typeof response.message === "string"
          ? response.message
          : "Failed to save notification settings.",
      );

      setSavingNotificationSettings(false);

      return false;
    },

    [],
  );

  useEffect(() => {
    if (!userSummary) {
      setNotificationSettingsLoading(false);

      return;
    }

    let cancelled = false;

    void (async () => {
      setNotificationSettingsLoading(true);

      setNotificationSettingsLoadError(null);

      const response = await getNotificationSettings();

      if (cancelled) {
        return;
      }

      if (response.success && response.data) {
        setShowNotifications(response.data.show_notifications);
      } else {
        setNotificationSettingsLoadError(
          typeof response.message === "string"
            ? response.message
            : "Failed to load notification settings.",
        );
      }

      setNotificationSettingsLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [userSummary]);

  useEffect(() => {
    const socketUrl = getApiBaseUrl();

    if (!socketUrl || socketUserKey === null) return;

    // socket.io calls the auth function on the initial connect and on every
    // reconnect, so each handshake carries a freshly acquired (non-expired)
    // access token rather than the one captured when the effect first ran.
    const socket: Socket = io(socketUrl, {
      auth: (cb) => {
        getAccessToken()
          .then((token) => cb({ Authorization: `Bearer ${token}` }))
          .catch((error: unknown) => {
            console.error("Failed to get socket access token:", error);
            cb({});
          });
      },
    });

    const notifyListeners = (event: SocketEventName, data?: unknown) => {
      for (const listener of listenersRef.current.get(event) ?? []) {
        try {
          listener(data);
        } catch (error) {
          console.error(`Socket ${event} listener failed:`, error);
        }
      }
    };

    socket.on("connect", () => {
      console.log("Connected");

      setIsConnected(true);
      notifyListeners("connect");
    });

    socket.on("disconnect", () => {
      setIsConnected(false);
    });

    for (const event of FORWARDED_SOCKET_EVENTS) {
      socket.on(event, (data: unknown) => notifyListeners(event, data));
    }

    socket.on("deal_updated", (data: unknown) => {
      setLastMessage((prev) => [...prev, data].slice(-MAX_LAST_MESSAGES));
      handleDealUpdatedRef.current(data);
      notifyListeners("deal_updated", data);

      const timeoutId = setTimeout(() => {
        pendingNotificationTimeoutsRef.current =
          pendingNotificationTimeoutsRef.current.filter(
            (pendingId) => pendingId !== timeoutId,
          );

        setLastMessage((prev) => removeMessage(prev, data));

        if (
          typeof data === "object" &&
          data !== null &&
          dismissedDealUpdatesRef.current.has(data)
        ) {
          return;
        }

        // Queue this exact payload; looking it up by deal_id would pick the
        // oldest message for the deal and show stale changes.
        queueDealUpdateNotificationRef.current(data);
      }, DEAL_UPDATE_NOTIFICATION_DELAY_MS);

      pendingNotificationTimeoutsRef.current.push(timeoutId);
    });

    return () => {
      for (const timeoutId of pendingNotificationTimeoutsRef.current) {
        clearTimeout(timeoutId);
      }

      pendingNotificationTimeoutsRef.current = [];

      socket.disconnect();
    };
  }, [socketUserKey]);

  return (
    <SocketContext.Provider
      value={{
        isConnected,
        lastMessage,
        userSummary,
        showNotifications,
        notificationSettingsLoading,
        notificationSettingsLoadError,
        savingNotificationSettings,
        saveNotificationSettingsError,
        saveNotificationSettings,
        handleDealUpdated,
        subscribe,
        suppressDealUpdateToasts,
      }}
    >
      <Toaster position="top-right" />

      {children}
    </SocketContext.Provider>
  );
}

function removeMessage(messages: unknown[], message: unknown): unknown[] {
  const index = messages.indexOf(message);

  if (index === -1) {
    return messages;
  }

  return [...messages.slice(0, index), ...messages.slice(index + 1)];
}

export function useSocket() {
  const context = useContext(SocketContext);

  if (!context) {
    throw new Error("useSocket must be used within a SocketProvider");
  }

  return context;
}
